import React, { useState, useEffect, SetStateAction, Dispatch } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';

import Typography from '@/components/atoms/Typography';
import Checkbox from '@/components/atoms/Checkbox';
import Button from '@/components/atoms/Button';

import mq from '@/styles/mq';

import { useProductFiltersContext } from '@/contexts/productFiltersContext';

import Category from '@/domain/entities/Category';

export interface CategoriesProps {
  primary?: Category[];
  secondary?: Category[];
  tertiary?: Category[];
}

interface ProductCategoriesProps {
  categories?: CategoriesProps;
}

const ProductCategoriesRoot = styled('div')`
  background-color: ${({ theme }) => theme.color.secondary.main};
  padding-top: ${({ theme }) => theme.spacing(1)};
  padding-bottom: ${({ theme }) => theme.spacing(5)};
  padding-left: ${({ theme }) => theme.spacing(1)};
  padding-right: ${({ theme }) => theme.spacing(1)};
  ${mq('sm')} {
    padding-top: ${({ theme }) => theme.spacing(3)};
    padding-bottom: ${({ theme }) => theme.spacing(12)};
    padding-left: ${({ theme }) => theme.spacing(3)};
    padding-right: ${({ theme }) => theme.spacing(3)};
  }
  border-radius: 10px;
`;
const Title = styled(Typography)`
  font-weight: 800;
`;
const TitleSecondary = styled(Title)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
const TitleTertiary = styled(Title)`
  margin-top: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
const ResetContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const ResetButton = styled(Button)`
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const ResetIcon = styled(FontAwesomeIcon)``;
const CategoriesContainer = styled('div')`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding-left: ${({ theme }) => theme.spacing(0.5)};
`;
const CategoryRoot = styled('div')`
  margin-bottom: ${({ theme }) => theme.spacing(1.5)};
`;
const Name = styled(Typography)``;
const ActiveFilter = css`
  font-weight: 800;
`;

const ProductCategoryPrimary = ({
  id,
  name,
  setIsPrimaryChecked,
  isPrimaryChecked,
}: Category): JSX.Element => {
  const { productFilters, setProductFilters } = useProductFiltersContext();

  const isActive = id ? productFilters.includes(id) : false;

  // const handleCheck = () => {
  //   if (!isActive && id) setProductFilters([...productFilters, id]);
  //   else setProductFilters(productFilters.filter((el) => el !== id));
  // };
  const handleCheck = () => {
    if (!isActive && id) setProductFilters([id]);
    else setProductFilters(productFilters.filter((el) => el !== id));
  };

  return (
    <CategoryRoot>
      <Checkbox
        id={`category-${id}`}
        //onChange={handleCheck}
        checked={id === isPrimaryChecked}
        onClick={() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isPrimaryChecked !== id
            ? setIsPrimaryChecked(id as string)
            : setIsPrimaryChecked('');
        }}
      >
        <Name
          color={isPrimaryChecked === id ? 'textPrimary' : 'textSecondary'}
          css={isPrimaryChecked === id ? ActiveFilter : css``}
        >
          {name}
        </Name>
      </Checkbox>
    </CategoryRoot>
  );
};

const ProductCategorySecondary = ({
  id,
  name,
  setIsSecondaryChecked,
  isSecondaryChecked,
}: Category): JSX.Element => {
  const { productFilters, setProductFilters } = useProductFiltersContext();

  const isActive = id ? productFilters.includes(id) : false;

  // const handleCheck = () => {
  //   if (!isActive && id) setProductFilters([...productFilters, id]);
  //   else setProductFilters(productFilters.filter((el) => el !== id));
  // };
  const handleCheck = () => {
    if (!isActive && id) setProductFilters([id]);
    else setProductFilters(productFilters.filter((el) => el !== id));
  };

  return (
    <CategoryRoot>
      <Checkbox
        id={`category-${id}`}
        //onChange={handleCheck}
        checked={id === isSecondaryChecked}
        onClick={() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isSecondaryChecked !== id
            ? setIsSecondaryChecked(id as string)
            : setIsSecondaryChecked('');
        }}
      >
        <Name
          color={isSecondaryChecked === id ? 'textPrimary' : 'textSecondary'}
          css={isSecondaryChecked === id ? ActiveFilter : css``}
        >
          {name}
        </Name>
      </Checkbox>
    </CategoryRoot>
  );
};

const ProductCategoryTertiary = ({
  id,
  name,
  setIsTertiaryChecked,
  isTertiaryChecked,
}: Category): JSX.Element => {
  const { productFilters, setProductFilters } = useProductFiltersContext();

  //const isActive = id ? productFilters.includes(id) : false;

  // const handleCheck = () => {
  //   if (!isActive && id) setProductFilters([...productFilters, id]);
  //   else setProductFilters(productFilters.filter((el) => el !== id));
  // };

  return (
    <CategoryRoot>
      <Checkbox
        id={`category-${id}`}
        //onChange={handleCheck}
        checked={id === isTertiaryChecked}
        onClick={() => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          isTertiaryChecked !== id
            ? setIsTertiaryChecked(id as string)
            : setIsTertiaryChecked('');
        }}
      >
        <Name
          color={isTertiaryChecked === id ? 'textPrimary' : 'textSecondary'}
          css={isTertiaryChecked === id ? ActiveFilter : css``}
        >
          {name}
        </Name>
      </Checkbox>
    </CategoryRoot>
  );
};

const SecondaryCategories = ({
  categories,
  isSecondaryChecked,
  setIsSecondaryChecked,
}): JSX.Element => {
  return (
    <CategoriesContainer>
      <TitleSecondary variant="textLg">Sous-catégories</TitleSecondary>
      {categories.map((category, i) => (
        <ProductCategorySecondary
          key={i.toString()}
          {...category}
          setIsSecondaryChecked={setIsSecondaryChecked}
          isSecondaryChecked={isSecondaryChecked}
        />
      ))}
    </CategoriesContainer>
  );
};

const TertiaryCategories = ({
  categories,
  isTertiaryChecked,
  setIsTertiaryChecked,
}): JSX.Element => {
  return (
    <CategoriesContainer>
      <TitleTertiary variant="textLg">Type</TitleTertiary>
      {categories.map((category, i) => (
        <ProductCategoryTertiary
          key={i.toString()}
          {...category}
          setIsTertiaryChecked={setIsTertiaryChecked}
          isTertiaryChecked={isTertiaryChecked}
        />
      ))}
    </CategoriesContainer>
  );
};

const PrimaryCategories = ({
  categories,
  isPrimaryChecked,
  setIsPrimaryChecked,
  isSecondaryChecked,
  setIsSecondaryChecked,
  isTertiaryChecked,
  setIsTertiaryChecked,
}): JSX.Element => {
  return (
    <>
      <CategoriesContainer>
        {categories?.map((category, i) => (
          <ProductCategoryPrimary
            key={i.toString()}
            {...category}
            setIsPrimaryChecked={setIsPrimaryChecked}
            isPrimaryChecked={isPrimaryChecked}
          />
        ))}
      </CategoriesContainer>
      {isPrimaryChecked !== '' &&
        categories?.map((category: any, i: number) => {
          if (
            category?.id === isPrimaryChecked &&
            category?.secondary?.length > 0
          ) {
            return (
              <SecondaryCategories
                categories={category?.secondary}
                isSecondaryChecked={isSecondaryChecked}
                setIsSecondaryChecked={setIsSecondaryChecked}
                key={i.toString()}
              />
            );
          }
          return <div />;
        })}
      {isSecondaryChecked !== '' &&
        categories?.map((category: any) => {
          return category?.secondary?.map((item: any, index: number) => {
            if (item?.id === isSecondaryChecked && item?.tertiary?.length > 0) {
              return (
                <TertiaryCategories
                  categories={item?.tertiary}
                  isTertiaryChecked={isTertiaryChecked}
                  setIsTertiaryChecked={setIsTertiaryChecked}
                  key={index.toString()}
                />
              );
            }
            return <div />;
          });
        })}
    </>
  );
};

const ProductCategories = ({
  categories,
}: ProductCategoriesProps): JSX.Element => {
  const { productFilters, setProductFilters } = useProductFiltersContext();
  const [isPrimaryChecked, setIsPrimaryChecked] = useState<string>('');
  const [isSecondaryChecked, setIsSecondaryChecked] = useState<string>('');
  const [isTertiaryChecked, setIsTertiaryChecked] = useState<string>('');

  useEffect(() => {
    setProductFilters([]);
    setIsPrimaryChecked('');
    const id = localStorage.getItem('categoryId');
    setIsPrimaryChecked(id as string);
    setTimeout(() => localStorage.setItem('categoryId', ''), 2000);
  }, []);

  useEffect(() => {
    setIsSecondaryChecked('');
    setIsTertiaryChecked('');
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isPrimaryChecked !== ''
      ? setProductFilters([isPrimaryChecked])
      : setProductFilters([]);
  }, [isPrimaryChecked]);

  useEffect(() => {
    setIsTertiaryChecked('');
    if (isPrimaryChecked !== '' && isSecondaryChecked !== '') {
      setProductFilters([isPrimaryChecked, isSecondaryChecked]);
    } else if (isPrimaryChecked !== '' && isSecondaryChecked === '') {
      setProductFilters([isPrimaryChecked]);
    }
  }, [isSecondaryChecked]);

  useEffect(() => {
    if (
      isPrimaryChecked !== '' &&
      isSecondaryChecked !== '' &&
      isTertiaryChecked !== ''
    ) {
      setProductFilters([
        isPrimaryChecked,
        isSecondaryChecked,
        isTertiaryChecked,
      ]);
    } else if (
      isPrimaryChecked !== '' &&
      isSecondaryChecked !== '' &&
      isTertiaryChecked === ''
    ) {
      setProductFilters([isPrimaryChecked, isSecondaryChecked]);
    }
  }, [isTertiaryChecked]);

  const handleResetFilters = () => {
    setProductFilters([]);
    setIsPrimaryChecked('');
    setIsSecondaryChecked('');
    setIsTertiaryChecked('');
  };

  return (
    <ProductCategoriesRoot id="ProductCategoriesRoot">
      <ResetContainer>
        <Title variant="textLg">Catégories</Title>
        {productFilters.length > 0 && (
          <ResetButton
            variant="fab"
            color="primary"
            size="sm"
            onClick={handleResetFilters}
          >
            <ResetIcon icon={faTimes} />
          </ResetButton>
        )}
      </ResetContainer>
      <PrimaryCategories
        categories={categories}
        isPrimaryChecked={isPrimaryChecked}
        setIsPrimaryChecked={setIsPrimaryChecked}
        isSecondaryChecked={isSecondaryChecked}
        setIsSecondaryChecked={setIsSecondaryChecked}
        isTertiaryChecked={isTertiaryChecked}
        setIsTertiaryChecked={setIsTertiaryChecked}
      />
      {/* <CategoriesContainer>{PrimaryCategories}</CategoriesContainer> */}
      {/* <TitleSub variant="textLg">Sous-catégorie</TitleSub>
      <CategoriesContainer>{SecondaryCategories}</CategoriesContainer>
      <TitleTertiary variant="textLg">Type</TitleTertiary>
      <CategoriesContainer>{TertiaryCategories}</CategoriesContainer> */}
    </ProductCategoriesRoot>
  );
};

export default ProductCategories;
